const OFFLINE_AUTH_STORAGE_KEY = 'archon_offline_auth_v1';
const OFFLINE_SESSION_PREFIX = 'offline-session:';
const OFFLINE_LOGIN_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function normalizeTenant(tenant) {
  return String(tenant || '').trim().toLowerCase();
}

function normalizeUsername(username) {
  return String(username || '').trim().toLowerCase();
}

function buildLeaseKey(tenant, username) {
  return `${normalizeTenant(tenant)}::${normalizeUsername(username)}`;
}

function readOfflineAuthStore() {
  try {
    const raw = localStorage.getItem(OFFLINE_AUTH_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeOfflineAuthStore(store) {
  try {
    localStorage.setItem(OFFLINE_AUTH_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore quota errors
  }
}

function randomSaltBase64() {
  if (typeof window === 'undefined' || !window.crypto?.getRandomValues) {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function sha256Hex(value) {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    throw new Error('Offline auth is not supported in this browser.');
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function hashInput(tenant, username, password, salt) {
  return `${normalizeTenant(tenant)}|${normalizeUsername(username)}|${String(password || '')}|${salt}`;
}

function pruneExpiredEntries(store) {
  const now = Date.now();
  const next = { ...store };
  Object.keys(next).forEach((key) => {
    const record = next[key];
    if (!record || !record.expires_at || Number(record.expires_at) <= now) {
      delete next[key];
    }
  });
  return next;
}

export function isOfflineSessionToken(token) {
  return typeof token === 'string' && token.startsWith(OFFLINE_SESSION_PREFIX);
}

export function parseOfflineSessionToken(token) {
  if (!isOfflineSessionToken(token)) return null;
  try {
    const encoded = token.slice(OFFLINE_SESSION_PREFIX.length);
    const payload = JSON.parse(atob(encoded));
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

export function createOfflineSessionToken({ tenant, username }) {
  const payload = {
    tenant: normalizeTenant(tenant),
    username: normalizeUsername(username),
    issued_at: Date.now(),
    nonce: Math.random().toString(36).slice(2, 10),
  };
  return `${OFFLINE_SESSION_PREFIX}${btoa(JSON.stringify(payload))}`;
}

export async function saveOfflineLoginLease({
  tenant,
  username,
  password,
  userSnapshot = null,
}) {
  const key = buildLeaseKey(tenant, username);
  if (!key || !password) return null;

  const salt = randomSaltBase64();
  const password_hash = await sha256Hex(hashInput(tenant, username, password, salt));
  const now = Date.now();

  const store = pruneExpiredEntries(readOfflineAuthStore());
  store[key] = {
    tenant: normalizeTenant(tenant),
    username: normalizeUsername(username),
    salt,
    password_hash,
    issued_at: now,
    last_online_at: now,
    expires_at: now + OFFLINE_LOGIN_TTL_MS,
    user_snapshot: userSnapshot && typeof userSnapshot === 'object' ? userSnapshot : null,
  };
  writeOfflineAuthStore(store);
  return store[key];
}

export async function validateOfflineLogin({ tenant, username, password }) {
  const key = buildLeaseKey(tenant, username);
  if (!key) return { ok: false, reason: 'missing_identity' };
  const store = pruneExpiredEntries(readOfflineAuthStore());
  writeOfflineAuthStore(store);

  const lease = store[key];
  if (!lease) return { ok: false, reason: 'missing_lease' };
  if (!lease.expires_at || lease.expires_at <= Date.now()) {
    return { ok: false, reason: 'expired_lease' };
  }
  if (!password) return { ok: false, reason: 'missing_password' };

  const hash = await sha256Hex(hashInput(tenant, username, password, lease.salt));
  if (hash !== lease.password_hash) {
    return { ok: false, reason: 'invalid_password' };
  }

  return {
    ok: true,
    expires_at: lease.expires_at,
    last_online_at: lease.last_online_at || lease.issued_at || null,
    user_snapshot: lease.user_snapshot || null,
  };
}

export function getOfflineLoginErrorMessage(reason) {
  if (reason === 'expired_lease') {
    return 'Offline access expired. Connect to the internet and log in again to renew 24-hour access.';
  }
  if (reason === 'invalid_password') {
    return 'Offline login failed. Invalid password for this device cache.';
  }
  if (reason === 'missing_lease') {
    return 'Offline login not available yet. Connect once and sign in to enable 24-hour offline access.';
  }
  return 'Offline login is unavailable. Connect to the internet and try again.';
}

export function getOfflineLoginTtlMs() {
  return OFFLINE_LOGIN_TTL_MS;
}
